'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';

/**
 * 메인 배너 컴포넌트
 *
 * 홈페이지 상단에 표시되는 메인 배너 컴포넌트입니다.
 * 전체 너비 자동 슬라이드 배너
 *
 * 주요 기능:
 * - 전체 너비 슬라이드 배너 (5초 간격)
 * - 페이드 전환 효과
 * - 호버 인터랙션
 * - 반응형 디자인
 */
export function MainBanner() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const slides = [
    '/banner/banner_01.jpg',
    '/banner/banner_02.jpg',
    '/banner/banner_03.png',
  ];

  // 애니메이션 초기화 및 슬라이드 자동 전환
  useEffect(() => {
    setIsMounted(true);

    const animationTimer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    // 5초마다 슬라이드 전환
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => {
      clearTimeout(animationTimer);
      clearInterval(slideInterval);
    };
  }, [slides.length]);

  // 서버와 클라이언트 초기 상태를 일치시키기 위한 체크
  const shouldShowAnimation = isMounted && isVisible;

  return (
    <section className="w-full bg-gray-50 pt-28 md:pt-32 pb-20 md:pb-24 lg:pb-28">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-8">
        {/* 전체 너비 배너 */}
        <div
          className={`relative overflow-hidden rounded-2xl shadow-2xl h-[500px] lg:h-[520px] group transition-all duration-700 ${
            shouldShowAnimation
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4'
          }`}
        >
          {/* 배경 이미지 슬라이더 */}
          <div className="absolute inset-0 w-full h-full">
            {slides.map((slide, index) => (
              <div
                key={slide}
                className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <Image
                  src={slide}
                  alt={`RISE 사업단 배너 ${index + 1}`}
                  fill
                  className="object-cover transform group-hover:scale-105 transition-transform duration-700"
                  priority={index === 0}
                  quality={90}
                />
              </div>
            ))}

            {/* 그라데이션 오버레이 */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          </div>

          {/* 텍스트 콘텐츠 */}
          <div className="relative z-10 h-full flex flex-col justify-end p-8 lg:p-12">
            <div className="space-y-4">
              <h1 className="text-3xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight drop-shadow-2xl">
                충북대학교
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                  RISE사업단
                </span>
              </h1>

              <p className="text-sm lg:text-lg text-gray-200 font-medium max-w-3xl leading-relaxed mb-12">
                지역사회 수요와 대학의 특성화된 강점을 결합하여 지역과 대학의
                동반 성장을 견인하는 개방형 혁신과 공진화를 기반으로 한
                지산학연협력 가치창출 플랫폼
              </p>
            </div>

            {/* 슬라이드 인디케이터 */}
            <div className="absolute bottom-8 left-8 flex gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'bg-white w-12'
                      : 'bg-white/40 hover:bg-white/60 w-8'
                  }`}
                  aria-label={`슬라이드 ${index + 1}로 이동`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
